//src/desktop/ui/components/generalinfoview/js

import { lookupSensorFallback } from "./generalinfo.api.js";
import { fetchTyre, fetchTyreActions } from "../../tyres/views/tyres.api.js";
import { exportSensorReportExcel } from '../../unit-sensors/views/unitdiagrams/reports/sensorexcelreport.js';
import { getSimUsageTotals, getThisMonthDailyUsage, getLast3FullMonthsTotals, getUsageDetailsRange } from "../../unit-sensors/views/unitdiagrams/repeaters/simcontrol.api.js";

import excelExportIconUrl from '../../../../assets/icons/excel-export.svg';

let isGeneralInfoFormOpen = false;

const WARRANTY_DAYS = 1000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const dataUsageLabelMap = {
  dataUsageMB: 'Data Usage (MB)',
};

const wheelSensorLabelMap = {
  Id: 'Wheel Sensor Id',
  wheelId: 'Wheel Sensor Position (Paired)',
  currentPressure: 'Pressure',
  currentTemperature: 'Temperature',
  // highestTemperature: 'Highest Temperature',
  // highestTemperatureTS: 'Highest Temperature Timestamp',
  currentVoltage: 'Voltage',
  currentRotation: 'Rotation',
  firstPayloadTS: 'First Timestamp Received',
  payloadTS: 'Pressure Timestamp Sent',
  receivedTS: 'Pressure Timestamp Received',
  firmwareVersion: 'Firmware Version',
  nbPayloadTS: 'Pairing Timestamp Sent',
  nbReceivedTS: 'Pairing Timestamp Received'
};

const locationSensorLabelMap = {
  Id: 'Location Sensor Id',
  repeaterId: 'Location Sensor Id',
  unitName: 'Unit Name',
  unitType: 'Unit Type',
  location: 'Location Sensor Position',
  voltage: 'Voltage',
  payloadTS: 'Payload Timestamp',
  firstPayloadTS: 'First Timestamp Received',
  assignedToUnitTS: 'First Assigned Timestamp',
  receivedTS: 'Received Timestamp',
  wheel: 'Wheel Configuration',
  firmwareVersion: 'Firmware Version',
};

// latestPayloadTS: ' Latest Payload Timestamp',
// latestReceivedTS: 'Latest Received Timestamp',
// firstRecordTS: 'First Timestamp Received',

const signalBoosterLabelMap = {
  Id: 'Signal Booster Id',
  unitName: 'Unit Name',
  unitType: 'Unit Type',
  voltage: 'Voltage',
  payloadTS: 'Booster Payload Timestamp',
  firstPayloadTS: 'Booster First Timestamp Received',
  receivedTS: 'Booster Received Timestamp',
  firstRecordTS: 'Booster First Timestamp Received',
  firmwareVersion: 'Booster Firmware Version'
};


const unitLabelMap = {
  name: 'Unit Name',
  unitType: 'Unit Type',
};

const gatewayLabelMap = {
  imeiNumber: 'IMEI',
  gatewayBatteryVoltage: 'Battery Voltage',
  gatewayPowerSupplyVoltage: 'External Voltage',
  simCardNumber: 'SIM Card Number',
  firstReceivedTS: 'Gateway First Timestamp Received',
  timeUpdated: 'Gateway Time Updated',
  sinkId: 'Sink Number',
  sinkVoltage: 'Sink Voltage',
  sinkReceivedTS: 'Sink Time Updated',
  payloadTS: 'Gateway Payload Timestamp',
  firstPayloadTS: 'Sink First Timestamp Received',
  firmwareVersion: 'Firmware Version'
};

const tyreManagementFields = [
  {
    label: 'Tyre Id',
    keys: ['externalTyreId', 'tyreId', '_id', 'Id']
  },
  {
    label: 'Time Updated',
    keys: ['updatedAt']
  },
  {
    label: 'Total Km Completed',
    keys: ['tyreKm']
  },
  {
    label: 'Initial Tread Depth',
    keys: ['initialTyreTreadDepth']
  },
  {
    label: 'Tread Depth',
    keys: ['tyreTreadDepth']
  },
  {
    label: 'Tyre Brand',
    keys: ['tyreBrand']
  },
  {
    label: 'Tyre Size',
    keys: ['tyreSize']
  },
  {
    label: 'Tyre Type',
    keys: ['tyreType']
  },
  {
    label: 'Tyre Grade',
    keys: ['tyreGrade']
  },
  {
    label: 'Tyre Application',
    keys: ['tyreApplication']
  },
  {
    label: 'Wear Pattern',
    keys: ['wearPattern']
  },
  {
    label: 'Highest Tyre Temperature',
    keys: ['highestTemperature']
  },
  {
    label: 'Highest Temperature Timestamp',
    keys: ['highestTemperatureTS']
  },
  {
    label: 'Supplier Name',
    keys: ['supplier']
  },
  {
    label: 'Purchase Cost',
    keys: ['purchaseCost']
  },
  {
    label: 'Purchase Date',
    keys: ['purchaseDate']
  },
  {
    label: 'Notes',
    keys: ['notes']
  }
];

const isBlank = (value) => {
  return value === null || value === undefined || value === '';
};

const pickFirstValue = (source, keys) => {
  if (!source) return null;

  for (const key of keys) {
    if (!isBlank(source[key])) {
      return source[key];
    }
  }

  return null;
};

const normaliseTyreResponse = (payload) => {
  if (!payload) return null;

  if (payload.tyre) return payload.tyre;
  if (payload.result) return payload.result;

  if (payload.data) {
    return normaliseTyreResponse(payload.data);
  }

  if (Array.isArray(payload.results)) {
    return payload.results[0] || null;
  }

  return payload;
};

const resolveTyreLookupId = (item) => {
  return (
    item?.tyre?.externalTyreId ||
    item?.externalTyreId ||
    item?.tyreId ||
    item?.currentTyre?.externalTyreId ||
    null
  );
};

const addUnit = (value, unit) => {
  if (isBlank(value)) return value;

  const text = String(value);

  // Do not double-add units if the API already returns "6.45 Bar" etc.
  if (/[a-zA-Z°]/.test(text)) {
    return text;
  }

  if (unit.trim() === 'R') { return `${unit} ${text}`; }

  return `${text}${unit}`;
};

const extractNumber = (value) => {
  if (typeof value === 'number') return value;

  const match = String(value)
    .replace(',', '.')
    .match(/-?\d+(\.\d+)?/);

  return match ? Number(match[0]) : NaN;
};

const formatDecimal = (value, decimals = 2) => {
  const numberValue = extractNumber(value);

  if (!Number.isFinite(numberValue)) {
    return value;
  }

  return numberValue.toFixed(decimals);
};

const formatDecimalWithUnit = (value, unit, decimals = 2) => {
  if (isBlank(value)) return null;

  const numberValue = extractNumber(value);

  if (!Number.isFinite(numberValue)) {
    return value;
  }

  return `${numberValue.toFixed(decimals)}${unit}`;
};

const formatTyreInfoValue = (label, value) => {
  if (isBlank(value)) return null;

  if (label.includes('Timestamp')) {
    const date = new Date(value);

    if (!isNaN(date.getTime())) {
      const pad = (n) => String(n).padStart(2, '0');

      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }
  }

  if (label === 'Total Km Completed') {
    return addUnit(value, ' km');
  }

  if (label === 'Tread Depth' || label === 'Initial Tread Depth') {
    return addUnit(value, ' mm');
  }

  if (label.includes('Pressure')) {
    return formatDecimalWithUnit(value, ' bar', 2);
  }

  if (label.includes('Temperature')) {
    return addUnit(value, ' °C');
  }

  if (label.includes('Cost')) {
    return addUnit(value, 'R ');
  }

  return value;
};

const getArrayFromApiResponse = (payload) => {
  if (!payload) return [];

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  // if (Array.isArray(payload.results)) return payload.results;
  // if (Array.isArray(payload.items)) return payload.items;
  // if (Array.isArray(payload.actions)) return payload.actions;

  if (payload.data) return getArrayFromApiResponse(payload.data);

  return [];
};

const getLatestTyreAction = (actions = []) => {
  return [...actions].sort((a, b) => {
    const aDate = new Date(
      a.actionDate ||
      a.createdAt ||
      a.receivedTS ||
      a.timestamp ||
      0
    );

    const bDate = new Date(
      b.actionDate ||
      b.createdAt ||
      b.receivedTS ||
      b.timestamp ||
      0
    );

    return bDate - aDate;
  })[0] || null;
};

const loadTyreManagementInfo = async (item) => {
  const embeddedTyre =
    item?.tyre ||
    item?.currentTyre ||
    item?.fittedTyre ||
    null;

  const externalTyreId = resolveTyreLookupId(item);

  if (!externalTyreId && embeddedTyre && typeof embeddedTyre === 'object') {
    return {
      status: 'success',
      tyre: embeddedTyre
    };
  }

  if (!externalTyreId) {
    return {
      status: 'no-tyre',
      tyre: null,
      message: 'No tyre is linked to this wheel.'
    };
  }

  try {
    const [tyreResponse, actionsResponse] = await Promise.all([
      fetchTyre(externalTyreId),
      fetchTyreActions(externalTyreId)
    ]);

    console.log('TYRE RECORD RESPONSE:', tyreResponse);
    console.log('TYRE ACTIONS RESPONSE:', actionsResponse);

    if (!tyreResponse?.success) {
      return {
        status: 'error',
        tyre: null,
        message: tyreResponse?.message || 'Could not load tyre information.'
      };
    }

    const tyreRecord = normaliseTyreResponse(tyreResponse.data);
    const actions = actionsResponse?.success
      ? getArrayFromApiResponse(actionsResponse.data)
      : [];

    const latestAction = getLatestTyreAction(actions);

    return {
      status: 'success',
      tyre: {
        ...latestAction,
        ...tyreRecord,
        latestAction
      }
    };

  } catch (err) {
    console.warn('Failed to load tyre management info:', err);

    return {
      status: 'error',
      tyre: null,
      message: 'Could not load tyre information.'
    };
  }
};

const buildTyreManagementInfo = (tyreResult) => {
  const tyreInformation = document.createElement('div');
  tyreInformation.className = 'form-group';

  const tyreInfo = document.createElement('div');
  tyreInfo.className = 'sensor-info';

  if (!tyreResult || tyreResult.status !== 'success' || !tyreResult.tyre) {
    const note = document.createElement('div');
    note.style.color = '#ad0505';
    note.style.fontSize = '0.85rem';
    note.style.fontStyle = 'italic';
    note.textContent = tyreResult?.message || 'Tyre information not available.';

    tyreInfo.appendChild(note);
    tyreInformation.appendChild(tyreInfo);

    return tyreInformation;
  }

  const tyre = tyreResult.tyre;

  tyreManagementFields.forEach(fieldConfig => {
    const rawValue = pickFirstValue(tyre, fieldConfig.keys);
    const displayValue = formatTyreInfoValue(fieldConfig.label, rawValue);

    if (!isBlank(displayValue)) {
      const field = buildField(fieldConfig.label, displayValue);
      tyreInfo.appendChild(field);
    }
  });

  if (!tyreInfo.children.length) {
    const note = document.createElement('div');
    note.style.color = '#ad0505';
    note.style.fontSize = '0.85rem';
    note.style.fontStyle = 'italic';
    note.textContent = 'Tyre record found, but no display fields were available.';
    tyreInfo.appendChild(note);
  }

  tyreInformation.appendChild(tyreInfo);
  return tyreInformation;
};

const wheelTypes = new Set([
  'wheel',
  'spare',
  'unpaired',
  'trailer-wheel',
  'trailer-spare'
]);

const repeaterTypes = new Set([
  'repeater',
  'repeater-standalone',
  'signal-booster'
]);

const gatewayTypes = new Set([
  'gateway',
  'sink'
]);

const resolveSensorLookupId = (sensorType, item) => {
  if (!item) return null;

  if (wheelTypes.has(sensorType)) {
    return item.sensorId || item.Id || item.wheelId;
  }

  if (repeaterTypes.has(sensorType)) {
    return item.Id || item.repeaterId;
  }

  if (gatewayTypes.has(sensorType)) {
    return item.imei || item.imeiNumber || item.Id;
  }

  return item.Id || item.sensorId;
};

const enrichSensorFromAPI = async (sensorType, unit, item) => {
  const lookupId = resolveSensorLookupId(sensorType, item);

  if (!lookupId) {
    console.warn('No lookup ID found for sensor type:', sensorType);
    return { sensorType, unit, item, apiStatus: 'no-id' };
  }

  try {
    const apiData = await lookupSensorFallback(String(lookupId));

    if (!apiData) {
      console.warn('No API data returned for ID:', lookupId);
      return { sensorType, unit, item, apiStatus: 'no-data' };
    }

    // Merge strategy: API data takes precedence for detailed info
    // but keep local UI-specific properties
    const mergedItem = {
      ...item,           // Local data (positions, UI state)
      ...apiData.item,   // API data (firmware, detailed timestamps)
      // Explicitly preserve critical local properties if needed
      sensorType: sensorType // Keep original UI type designation
    };

    const mergedUnit = apiData.unit
      ? { ...unit, ...apiData.unit }
      : unit;

    return {
      sensorType: apiData.type || sensorType,
      unit: mergedUnit,
      item: mergedItem,
      apiStatus: 'success'
    };

  } catch (err) {
    console.error('Error enriching sensor from API:', err);
    return { sensorType, unit, item, apiStatus: 'error', error: err };
  }
};

/**
 * Fetches additional context data (repeater, gateway) in parallel
 * This ensures we get firmware versions for all related components
 */
const enrichRelatedSensorData = async (sensorType, unit, item) => {
  const fetchPromises = [];
  const results = {
    wheel: null,
    repeater: null,
    gateway: null,
    sink: null
  };

  // For wheel sensors, also fetch repeater and gateway data
  if (wheelTypes.has(sensorType)) {
    // Fetch repeater data if repeaterId exists
    if (item.repeaterId) {
      fetchPromises.push(
        lookupSensorFallback(String(item.repeaterId))
          .then(data => ({ context: 'repeater', data }))
          .catch(err => {
            console.warn('Failed to fetch repeater data:', err);
            return { context: 'repeater', data: null };
          })
      );
    }

    // Fetch gateway data if imei exists
    if (unit?.imeiNumber || unit?.imei) {
      const gatewayImei = unit.imeiNumber || unit.imei;
      fetchPromises.push(
        lookupSensorFallback(String(gatewayImei))
          .then(data => ({ context: 'gateway', data }))
          .catch(err => {
            console.warn('Failed to fetch gateway data:', err);
            return { context: 'gateway', data: null };
          })
      );
    }

    // Fetch sink data if sinkId exists
    if (unit?.sinkId) {
      fetchPromises.push(
        lookupSensorFallback(String(unit.sinkId))
          .then(data => ({ context: 'sink', data }))
          .catch(err => {
            console.warn('Failed to fetch sink data:', err);
            return { context: 'sink', data: null };
          })
      );
    }
  }

  // For repeater/signal-booster sensors, fetch gateway data
  if (repeaterTypes.has(sensorType)) {
    if (unit?.imeiNumber || unit?.imei) {
      const gatewayImei = unit.imeiNumber || unit.imei;
      fetchPromises.push(
        lookupSensorFallback(String(gatewayImei))
          .then(data => ({ context: 'gateway', data }))
          .catch(err => {
            console.warn('Failed to fetch gateway data:', err);
            return { context: 'gateway', data: null };
          })
      );
    }
  }

  // Wait for all fetches to complete
  if (fetchPromises.length > 0) {
    const fetchResults = await Promise.allSettled(fetchPromises);

    fetchResults.forEach(result => {
      if (result.status === 'fulfilled' && result.value.data) {
        results[result.value.context] = result.value.data;
      }
    });
  }

  return results;
};

export const openGeneralInfoForm = async (sensorType, unit, item) => {

  if (isGeneralInfoFormOpen) {
    console.log('General info form already opening, skipping duplicate call');
    return;
  }
  isGeneralInfoFormOpen = true;
  document.body.style.cursor = 'wait';

  const generalInfoModal = document.getElementById('generalinfomodal');
  //const generalInfoForm = document.getElementById('generalinfoform');
  const closeModal = document.getElementById('closegeneralinfomodal');
  const searchInput = document.getElementById('sensor-search');

  const body = document.getElementById('generalinfo-body');
  body.replaceChildren();

  closeModal.onclick = null;
  closeModal.onclick = function (e) {
    generalInfoModal.style.display = "none";
    searchInput.style.display = 'none';
    searchInput.value = '';
    e.stopImmediatePropagation();
    return false;
  };

  const wrapper = document.createElement('div');
  wrapper.style.display = 'flex';
  wrapper.style.flexWrap = 'wrap';
  wrapper.style.gap = '18px';
  wrapper.style.width = '100%';

  try {
    // Step 1: Enrich primary sensor data from API
    const enriched = await enrichSensorFromAPI(sensorType, unit, item);
    sensorType = enriched.sensorType;
    unit = enriched.unit;
    item = enriched.item;

    const btn = document.getElementById('btnexportsensorreportexcel');
    if (btn) {
      const isWheel = wheelTypes.has(sensorType);
      btn.disabled = !(isWheel && unit && (unit.imei || unit.unitId) && item?.wheelId);

      if (!btn.querySelector('img')) {
        const icon = document.createElement('img');
        icon.src = excelExportIconUrl;
        icon.alt = '';
        icon.width = 25;
        icon.height = 25;
        icon.style.verticalAlign = 'middle';
        btn.prepend(icon);
      }

      btn.onclick = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isWheel) return;

        // Ensure we pass something with wheelId (or a wheelId string)
        const wheelArg = item?.wheelId ? item : (item?.wheelId || item?.Id || item?.sensorId);

        if (!unit) {
          alert("Cannot export: unit not found for this sensor.");
          return;
        }
        if (!wheelArg) {
          alert("Cannot export: wheelId not available for this sensor.");
          return;
        }

        await exportSensorReportExcel(unit, wheelArg);
      };
    }


    // Step 2: Fetch related sensor data + tyre management data
    const [relatedData, tyreManagementResult] = await Promise.all([
      enrichRelatedSensorData(sensorType, unit, item),
      wheelTypes.has(sensorType)
        ? loadTyreManagementInfo(item)
        : Promise.resolve(null)
    ]);

    // Step 3: Merge related data into unit/item
    if (relatedData.repeater) {
      // Store repeater data for use in buildLocationSensorInfo
      item.repeaterData = relatedData.repeater.item;
    }

    if (relatedData.gateway) {
      // Merge gateway data into unit
      unit = {
        ...unit,
        gatewayData: relatedData.gateway.item
      };
    }

    if (relatedData.sink) {
      // Merge sink data into unit
      unit = {
        ...unit,
        sinkData: relatedData.sink.item
      };
    }


    // Step 4: Build UI columns based on sensor type
    if (wheelTypes.has(sensorType)) {
      wrapper.appendChild(
        buildSensorColumn('Tyre Information', buildTyreManagementInfo(tyreManagementResult))
      );

      wrapper.appendChild(
        buildSensorColumn('Wheel Sensor', buildWheelSensorInfo(item))
      );

      wrapper.appendChild(
        buildSensorColumn('Location Sensor & Unit', buildLocationSensorInfo(unit, item, 'wheel'))
      );

      wrapper.appendChild(
        buildSensorColumn('Gateway & Sink', buildGatewayInfo(unit, item))
      );
    }

    if (repeaterTypes.has(sensorType)) {
      wrapper.appendChild(
        buildSensorColumn('Location Sensor & Unit', buildLocationSensorInfo(unit, item, sensorType))
      );
      wrapper.appendChild(
        buildSensorColumn('Gateway & Sink', buildGatewayInfo(unit, item))
      );
    }

    // Step 4.5: SimControl usage (ONLY when user searched an IMEI -> gatewayTypes)
    let simUsageModel = null;

    if (gatewayTypes.has(sensorType)) {
      // Prefer SIM number (msisdn) from gateway data (the thing you're already showing)
      const simNumber =
        unit?.gatewayData?.simCardNumber ||
        unit?.gateway?.simCardNumber ||
        unit?.simCardNumber;

      if (simNumber) {
        try {
          const [mtd, daily, last3] = await Promise.all([
            getSimUsageTotals({ msisdn: simNumber }),  // MTD totals
            getThisMonthDailyUsage(simNumber),         // daily this month
            getLast3FullMonthsTotals(simNumber),       // Dec/Jan/Feb totals + avg + labels
          ]);

          // Default selected month = most recent full month in last3
          const selectedIdx = (last3?.months?.length || 0) - 1;
          const selectedMonth = selectedIdx >= 0 ? last3.months[selectedIdx] : null;

          // Fetch daily detail bars for the selected month (date range)
          // This should return: { labels: ['1','2',...], values: [..], dates: ['YYYY-MM-DD', ...] }
          let selectedMonthDaily = null;
          if (selectedMonth?.startDate && selectedMonth?.endDate) {
            selectedMonthDaily = await getUsageDetailsRange({
              msisdn: simNumber,
              startDate: selectedMonth.startDate,
              endDate: selectedMonth.endDate
            });
          }

          simUsageModel = {
            msisdn: simNumber,
            mtdTotalMB: mtd?.dataUsageMB ?? 0,
            daily,
            avgLast3FullMonthsMB: last3?.avgLast3MB ?? null,
            rangeLabel: last3?.rangeLabel ?? "",
            monthly: {
              labels: last3.months.map(x => x.label),
              fullLabels: last3.months.map(x => x.fullLabel),
              values: last3.months.map(x => x.dataUsageMB ?? 0),
            },
            months: last3.months, // <-- add this (must contain startDate/endDate/key/fullLabel)
            selectedMonthLabel: selectedMonth?.fullLabel || "",
            selectedMonthDaily
          };

        } catch (e) {
          console.warn("SimControl usage fetch failed:", e);
          simUsageModel = null; // buildDataUsageInfo handles null
        }
      }
    }

    if (gatewayTypes.has(sensorType)) {
      wrapper.appendChild(
        buildSensorColumn('Unit', buildUnitInfo(unit, item))
      );
      wrapper.appendChild(
        buildSensorColumn('Gateway & Sink', buildGatewayInfo(unit, item))
      );
      wrapper.appendChild(
        buildSensorColumn('Data Usage', buildDataUsageInfo(simUsageModel && simUsageModel.error ? null : simUsageModel))
      );
    }

  } catch (err) {
    console.error('Error loading general info:', err);

    // Show error message but still render with available data
    const errorNote = document.createElement('div');
    errorNote.style.cssText = 'width: 100%; padding: 8px; background: #f8d7da; border-radius: 4px; font-size: 12px; color: #721c24;';
    errorNote.textContent = `⚠️ Error loading complete data: ${err.message}. Showing partial information.`;
    wrapper.prepend(errorNote);

    // Render with original data
    if (wheelTypes.has(sensorType)) {
      wrapper.appendChild(buildSensorColumn('Wheel Sensor', buildWheelSensorInfo(item)));
      wrapper.appendChild(buildSensorColumn('Location Sensor & Unit', buildLocationSensorInfo(unit, item, 'wheel')));
      wrapper.appendChild(buildSensorColumn('Gateway & Sink', buildGatewayInfo(unit, item)));
    }
    if (repeaterTypes.has(sensorType)) {
      wrapper.appendChild(buildSensorColumn('Location Sensor & Unit', buildLocationSensorInfo(unit, item, sensorType)));
      wrapper.appendChild(buildSensorColumn('Gateway & Sink', buildGatewayInfo(unit, item)));
    }
    if (gatewayTypes.has(sensorType)) {
      wrapper.appendChild(buildSensorColumn('Unit', buildUnitInfo(unit, item)));
      wrapper.appendChild(buildSensorColumn('Gateway & Sink', buildGatewayInfo(unit, item)));
    }
  }

  body.appendChild(wrapper);
  generalInfoModal.style.display = 'block';
  document.body.style.cursor = 'default';
  isGeneralInfoFormOpen = false;
};

const buildSensorColumn = (title, contentDiv) => {
  const col = document.createElement('div');
  col.className = 'sensor-column';
  const header = document.createElement('h6');
  header.textContent = title;
  col.appendChild(header);
  col.appendChild(contentDiv);
  return col;
};

const isFirstTimestampReceivedLabel = (labelText) => {
  return String(labelText || '')
    .toLowerCase()
    .includes('first timestamp received');
};

const startOfLocalDay = (date) => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const calculateWarrantyCounter = (timestampValue) => {
  const firstDate = new Date(timestampValue);

  if (isNaN(firstDate.getTime())) {
    return null;
  }

  const firstDay = startOfLocalDay(firstDate);
  const today = startOfLocalDay(new Date());

  const elapsedDays = Math.floor((today - firstDay) / MS_PER_DAY);

  // First Timestamp Received is Day 1
  const currentWarrantyDay = Math.max(1, elapsedDays + 1);

  if (currentWarrantyDay > WARRANTY_DAYS) {
    return `Expired - Day ${currentWarrantyDay} of ${WARRANTY_DAYS}`;
  }

  const daysRemaining = WARRANTY_DAYS - currentWarrantyDay;

  return `Day ${currentWarrantyDay} of ${WARRANTY_DAYS} (${daysRemaining} days remaining)`;
};

const buildWarrantyCounterField = (timestampValue, depth = 0) => {
  const warrantyText = calculateWarrantyCounter(timestampValue);

  if (!warrantyText) {
    return null;
  }

  const container = document.createElement('div');
  container.className = 'info-field warranty-counter-field';
  container.style.marginLeft = `${depth * 10}px`;

  const label = document.createElement('div');
  label.className = 'info-label';
  label.textContent = 'Warranty Counter ';

  const content = document.createElement('div');
  content.className = 'info-value';
  content.textContent = warrantyText;

  container.appendChild(label);
  container.appendChild(content);

  return container;
};

const isNumericValue = (value) => {
  return value !== null &&
    value !== undefined &&
    value !== '' &&
    !Array.isArray(value) &&
    !isNaN(Number(value));
};

// const roundToTwoDecimals = (value) => {
//   if (!isNumericValue(value)) return value;
//   return Number(value).toFixed(2);
// };

const formatGeneralInfoDisplayValue = (labelText, value) => {
  const label = String(labelText || '').toLowerCase();

  const isPressureValue =
    label.includes('pressure') &&
    !label.includes('timestamp');

  if (isPressureValue) {
    return formatDecimalWithUnit(value, ' bar', 2);
  }

  return value;
};

const buildField = (key, value, parentKey = '', depth = 0) => {
  const labelText = parentKey ? `${parentKey}.${key} ` : `${key} `;

  const container = document.createElement('div');
  container.className = 'info-field';
  container.style.marginLeft = `${depth * 10}px`;

  const label = document.createElement('div');
  label.className = 'info-label';
  label.textContent = labelText;

  const content = document.createElement('div');
  content.className = 'info-value';

  const formatTimestamp = (value) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  if (typeof value === 'boolean') {
    content.innerHTML = value ? '✓' : '✗';
    container.appendChild(label);
    container.appendChild(content);

  } else if (value instanceof Date || (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/))) {
    content.textContent = formatTimestamp(value);
    container.appendChild(label);
    container.appendChild(content);

  } else if (Array.isArray(value)) {
    content.textContent = `[${value.join(', ')}]`;
    container.appendChild(label);
    container.appendChild(content);

  } else if (typeof value === 'object' && value !== null) {
    const summary = document.createElement('details');
    summary.open = false;

    const summaryTitle = document.createElement('summary');
    summaryTitle.textContent = parentKey ? `${parentKey}.${key}` : key;
    summary.appendChild(summaryTitle);

    for (const nestedKey in value) {
      const nestedField = buildField(nestedKey, value[nestedKey], '', depth + 1);
      summary.appendChild(nestedField);
    }

    container.appendChild(summary);

  } else {
    const displayValue = formatGeneralInfoDisplayValue(labelText, value);

    content.textContent =
      displayValue !== null && displayValue !== undefined
        ? displayValue
        : 'null';

    container.appendChild(label);
    container.appendChild(content);
  }

  const fragment = document.createDocumentFragment();
  fragment.appendChild(container);

  if (isFirstTimestampReceivedLabel(labelText)) {
    const warrantyField = buildWarrantyCounterField(value, depth);

    if (warrantyField) {
      fragment.appendChild(warrantyField);
    }
  }

  return fragment;
};

const formatMBorGB = (mb) => {
  const n = Number(mb) || 0;
  return n >= 1024 ? `${(n / 1024).toFixed(2)} GB` : `${n.toFixed(2)} MB`;
};

const PRIMARY_COLOR = "#185785"; // <-- your primary (change if needed)

const destroyChartOnCanvas = (canvas) => {
  try {
    const existing = Chart.getChart(canvas);
    if (existing) existing.destroy();
  } catch (_) { }
};

const renderBarChart = (canvas, labels, values, dates, { titleEl, titleText } = {}) => {
  if (titleEl && titleText) titleEl.textContent = titleText;

  destroyChartOnCanvas(canvas);

  if (!labels?.length) return;

  const vals = values || [];

  const rollingAvg = (i) => {
    const start = Math.max(0, i - 6);
    const window = vals.slice(start, i + 1);
    return window.reduce((a, x) => a + x, 0) / (window.length || 1);
  };

  new Chart(canvas.getContext("2d"), {
    type: "bar",
    data: {
      labels,
      datasets: [{
        data: vals,
        backgroundColor: PRIMARY_COLOR,   // <-- bars colour
        borderColor: PRIMARY_COLOR
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const idx = items?.[0]?.dataIndex ?? 0;
              return dates?.[idx] || `Day ${labels[idx]}`;
            },
            label: (ctx) => {
              const v = ctx.parsed.y || 0;
              const idx = ctx.dataIndex;
              const avg7 = rollingAvg(idx);
              const lines = [`Data: ${formatMBorGB(v)}`];

              if (avg7 > 0) {
                const pct = ((v - avg7) / avg7) * 100;
                lines.push(`vs avg: ${pct.toFixed(0)}%`);
                if (v > avg7 * 1.2) lines.push("⚠ Above trend");
              }
              return lines;
            }
          }
        }
      },
      scales: {
        x: { ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10 } },
        y: { beginAtZero: true }
      }
    }
  });
};

const makeSparkline = (canvas, labels, values, { tooltipLabelFn, spikeIndexSet, onPointClick, primaryColour = '#185785', datasetLabel = "" } = {}) => {
  return new Chart(canvas.getContext("2d"), {
    type: "line",
    data: {
      labels,
      datasets: [{
        label: datasetLabel,
        data: values,
        tension: 0.35,
        borderWidth: 1.25,
        fill: false,
        borderColor: primaryColour,
        pointBackgroundColor: primaryColour,
        pointBorderColor: primaryColour,
        pointRadius: (ctx) => (spikeIndexSet?.has(ctx.dataIndex) ? 4 : 2.5),
        pointHoverRadius: 12,
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          //position: 'nearest',
          yAlign: 'bottom',
          xAlign: 'center',
          caretPadding: 18,
          callbacks: {
            title: () => null, // <-- no title, just the label"",
            label: tooltipLabelFn || ((ctx) => `Data: ${formatMBorGB(ctx.parsed.y || 0)}`)
          }
        }
      },
      onClick: (evt, elements) => {
        if (!elements?.length) return;
        const idx = elements[0].index;
        onPointClick?.(idx);
      },
      //interaction: { mode: "index", intersect: false },
      scales: { x: { display: false }, y: { display: false } },
      elements: { point: { hitRadius: 12 } }
    }
  });
};

const monthDailyCache = new Map(); // key: "2026-02" -> {labels, values, dates}

let activeBarChart = null;

const destroyChart = (chart) => {
  try { chart?.destroy?.(); } catch (_) { }
};

const renderDailyBarChart = (barCanvas, barTitleEl, monthLabel, dailyModel) => {
  // Update title
  barTitleEl.textContent = monthLabel ? `Daily Usage (${monthLabel})` : "Daily Usage";

  // If no data, show empty chart area
  if (!dailyModel?.labels?.length) {
    destroyChart(activeBarChart);
    activeBarChart = null;
    return;
  }

  destroyChart(activeBarChart);

  const labels = dailyModel.labels;
  const vals = dailyModel.values || [];
  const dates = dailyModel.dates || [];

  const rollingAvg = (i) => {
    const start = Math.max(0, i - 6);
    const window = vals.slice(start, i + 1);
    return window.reduce((a, x) => a + x, 0) / (window.length || 1);
  };

  activeBarChart = new Chart(barCanvas.getContext("2d"), {
    type: "bar",
    data: {
      labels,
      datasets: [{
        label: "MB",
        data: vals,
        backgroundColor: PRIMARY_COLOR,
        borderColor: PRIMARY_COLOR
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const idx = items?.[0]?.dataIndex ?? 0;
              return dates[idx] || `Day ${labels[idx]}`;
            },
            label: (ctx) => {
              const v = ctx.parsed.y || 0;
              const idx = ctx.dataIndex;
              const avg7 = rollingAvg(idx);
              const lines = [`Data: ${formatMBorGB(v)}`];

              if (avg7 > 0) {
                const pct = ((v - avg7) / avg7) * 100;
                lines.push(`vs avg: ${pct.toFixed(0)}%`);
                if (v > avg7 * 1.2) lines.push("⚠ Above trend");
              }
              return lines;
            }
          }
        }
      },
      scales: {
        x: { ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10 } },
        y: { beginAtZero: true }
      }
    }
  });
};

export const buildDataUsageInfo = (model) => {
  const wrap = document.createElement("div");
  wrap.className = "form-group";

  const info = document.createElement("div");
  info.className = "sensor-info";

  if (!model) {
    const note = document.createElement("div");
    note.style.fontSize = "0.85rem";
    note.style.opacity = "0.8";
    note.textContent = "Sim usage not available right now (SimControl timeout).";

    info.appendChild(note);
    wrap.appendChild(info);
    return wrap;
  }

  // ---------- helpers ----------
  const makeRow = () => {
    const row = document.createElement("div");
    row.style.display = "flex";
    row.style.alignItems = "center";
    row.style.justifyContent = "space-between";
    row.style.gap = "10px";
    row.style.marginTop = "8.5px";
    return row;
  };

  const makeSparkBox = (w = 150, h = 42) => {
    const box = document.createElement("div");
    box.style.width = `${w}px`;
    box.style.height = `${h}px`;
    box.style.flex = `0 0 ${w}px`;
    const canvas = document.createElement("canvas");
    box.appendChild(canvas);
    return { box, canvas };
  };

  const rangeLabel = model?.rangeLabel ? ` (${model.rangeLabel})` : "";

  // ---------- MTD heading ----------
  const mtdHeading = document.createElement("div");
  mtdHeading.style.marginTop = "6.5px";
  mtdHeading.style.fontSize = "0.925rem";
  mtdHeading.style.fontWeight = "650";
  mtdHeading.textContent = "Data Usage (Month-to-Date)";
  info.appendChild(mtdHeading);

  // ---------- MTD value ----------
  const mtdValue = document.createElement("div");
  mtdValue.style.fontSize = "0.85rem";
  //mtdValue.style.fontWeight = "600";
  mtdValue.textContent =
    model?.mtdTotalMB != null ? formatMBorGB(model.mtdTotalMB) : "—";
  info.appendChild(mtdValue);

  // ---------- MTD bar chart (daily this month) ----------
  const mtdBarBox = document.createElement("div");
  mtdBarBox.style.height = "125px";
  mtdBarBox.style.marginTop = "5px";
  const mtdBarCanvas = document.createElement("canvas");
  mtdBarBox.appendChild(mtdBarCanvas);
  info.appendChild(mtdBarBox);

  // ---------- 3-month avg + monthly sparkline side-by-side ----------
  // ---------- 3-month avg heading (row 1) ----------
  const avgHeading = document.createElement("div");
  avgHeading.style.marginTop = "10px";
  avgHeading.style.fontSize = "0.925rem";
  avgHeading.style.fontWeight = "650";
  avgHeading.textContent = `3-Month Average${rangeLabel}`;
  info.appendChild(avgHeading);

  const clickPoint = document.createElement("div");
  clickPoint.style.marginTop = "8.5px";
  clickPoint.style.fontSize = "0.65rem";
  clickPoint.style.color = PRIMARY_COLOR;
  clickPoint.textContent = `Click a data point in the sparkline to view daily bars for that month.`;
  info.appendChild(clickPoint);

  // ---------- 3-month avg value + sparkline (row 2) ----------
  const avgValueRow = makeRow();

  const avgValue = document.createElement("div");
  avgValue.style.flex = "1";
  avgValue.style.fontSize = "0.85rem";
  //avgValue.style.fontWeight = "600";
  avgValue.textContent =
    model?.avgLast3FullMonthsMB != null ? formatMBorGB(model.avgLast3FullMonthsMB) : "—";


  const { box: monthlyBox, canvas: monthlyCanvas } = makeSparkBox(160, 42);

  avgValueRow.appendChild(avgValue);
  avgValueRow.appendChild(monthlyBox);
  info.appendChild(avgValueRow);

  // ---------- Bar chart (daily bars for selected month) ----------
  const barTitle = document.createElement("div");
  barTitle.style.marginTop = "12px";
  barTitle.style.fontSize = "0.85rem";
  barTitle.style.fontWeight = "600";
  barTitle.textContent = model?.selectedMonthLabel
    ? `Daily Usage (${model.selectedMonthLabel})`
    : "Daily Usage (Selected Month)";

  const barBox = document.createElement("div");
  barBox.style.height = "120px";
  barBox.style.marginTop = "6px";

  const barCanvas = document.createElement("canvas");
  barBox.appendChild(barCanvas);

  info.appendChild(barTitle);
  info.appendChild(barBox);

  wrap.appendChild(info);

  renderDailyBarChart(
    barCanvas,
    barTitle,
    model?.selectedMonthLabel || "",
    model?.selectedMonthDaily || null
  );

  // ---------- Charts ----------

  // Render MTD bar chart from model.daily
  if (model?.daily?.labels?.length) {
    const labels = model.daily.labels;
    const vals = model.daily.values || [];
    const dates = model.daily.rows?.map(r => r.date) || []; // ["2026-03-01"...]
    renderBarChart(mtdBarCanvas, labels, vals, dates);
  }

  // Monthly sparkline (Dec/Jan/Feb totals)
  if (model?.monthly?.labels?.length) {
    const vals = model.monthly.values || [];
    const avg = model.avgLast3FullMonthsMB || 0;
    const spikeSet = new Set(
      vals.map((v, i) => (avg > 0 && v > avg * 1.2 ? i : -1)).filter(i => i >= 0)
    );

    makeSparkline(monthlyCanvas, model.monthly.labels, vals, {
      datasetLabel: "",
      spikeIndexSet: spikeSet,
      primaryColour: spikeSet.size > 0 ? '#dc3545' : '#185785',
      tooltipLabelFn: (ctx) => {
        const v = ctx.parsed.y || 0;
        const idx = ctx.dataIndex;
        const monthYear = model.monthly.fullLabels?.[idx] || ctx.label;

        const lines = [`${monthYear}: ${formatMBorGB(v)}`];

        // if (avg > 0) {
        //     const pct = ((v - avg) / avg) * 100;
        //     lines.push(`vs avg: ${pct.toFixed(0)}%`);
        //     if (v > avg * 1.2) lines.push("⚠ Above trend");
        // }
        // lines.push("Click to view daily bars");
        return lines;
      },

      onPointClick: async (idx) => {
        // We need month start/end for this index
        const monthMeta = model?.months?.[idx]; // IMPORTANT: add this to model (next section)

        if (!monthMeta?.startDate || !monthMeta?.endDate) return;

        const cacheKey = monthMeta.key || `${monthMeta.startDate}_${monthMeta.endDate}`;
        try {
          let daily = monthDailyCache.get(cacheKey);

          if (!daily) {
            // Optional loading indicator
            barTitle.textContent = `Daily Usage (${monthMeta.fullLabel}) - loading...`;

            daily = await getUsageDetailsRange({
              msisdn: model.msisdn,
              startDate: monthMeta.startDate,
              endDate: monthMeta.endDate
            });

            monthDailyCache.set(cacheKey, daily);
          }

          renderDailyBarChart(barCanvas, barTitle, monthMeta.fullLabel, daily);

        } catch (e) {
          console.warn("Failed to load month daily usage:", e);
          barTitle.textContent = `Daily Usage (${monthMeta.fullLabel}) - failed to load`;
          destroyChart(activeBarChart);
          activeBarChart = null;
        }
      }
    });
  }

  return wrap;
};

const buildGatewayInfo = (unit, item) => {
  const gatewayInformation = document.createElement('div');
  gatewayInformation.className = 'form-group';

  const gatewayInfo = document.createElement('div');
  gatewayInfo.className = 'sensor-info';

  // Build gateway source - explicitly control what goes in
  const source = {};

  // Priority order for gateway properties:
  // 1. unit.gatewayData (from API fetch)
  // 2. unit.gateway (nested in unit)
  // 3. unit itself (for direct properties like imeiNumber)

  const gatewayData = unit?.gatewayData || unit?.gateway || {};

  // Map gateway fields explicitly
  for (const key of Object.keys(gatewayLabelMap)) {
    let value = undefined;

    // Check in order of priority
    if (unit?.gatewayData?.[key] !== undefined) {
      value = unit.gatewayData[key];
    } else if (unit?.gateway?.[key] !== undefined) {
      value = unit.gateway[key];
    } else if (unit?.[key] !== undefined) {
      value = unit[key];
    } else if (unit?.sinkData?.[key] !== undefined) {
      value = unit.sinkData[key];
    }

    if (value !== undefined) {
      source[key] = value;
    }
  }

  // Render gateway fields
  for (const key in source) {
    if (gatewayLabelMap.hasOwnProperty(key)) {
      const displayKey = gatewayLabelMap[key];
      const field = buildField(displayKey, source[key]);
      gatewayInfo.appendChild(field);
    }
  }

  gatewayInformation.appendChild(gatewayInfo);
  return gatewayInformation;
};


const buildUnitInfo = (unit, item) => {
  const unitInformation = document.createElement('div');
  unitInformation.className = 'form-group';

  const unitInfo = document.createElement('div');
  unitInfo.className = 'sensor-info';

  for (const key in unit) {
    if (unitLabelMap.hasOwnProperty(key)) {
      const displayKey = unitLabelMap[key];
      const field = buildField(displayKey, unit[key]);
      unitInfo.appendChild(field);
    }
  }

  unitInformation.appendChild(unitInfo);
  return unitInformation;
};

const buildLocationSensorInfo = (unit, item, type) => {
  const locationSensorInformation = document.createElement('div');
  locationSensorInformation.className = 'form-group';

  const locationSensorInfo = document.createElement('div');
  locationSensorInfo.className = 'sensor-info';

  let repeater = null;

  if (type === 'wheel') {
    // For wheels, find repeater from unit.repeaters or use API-fetched data
    const repeaterId = item.repeaterId;
    repeater = item.repeaterData || unit?.repeaters?.find(r => r.Id === repeaterId);
  } else if (type === 'signal-booster') {
    // For signal boosters, the item itself is the sensor
    repeater = item;
  } else {
    // For standalone repeaters
    repeater = item;
  }

  if (repeater) {
    const labelMap = type === 'signal-booster' ? signalBoosterLabelMap : locationSensorLabelMap;

    for (const key in repeater) {
      if (labelMap.hasOwnProperty(key)) {
        const displayKey = labelMap[key];
        const field = buildField(displayKey, repeater[key]);
        locationSensorInfo.appendChild(field);
      }

      // Handle nested config.wheel property
      if (key === 'config' && repeater.config?.wheel !== undefined) {
        const field = buildField('Wheel Configuration', repeater.config.wheel);
        locationSensorInfo.appendChild(field);
      }
    }

  } else {
    const notFound = document.createElement('div');
    notFound.textContent = `Location Sensor not found.`;
    notFound.style.color = '#ad0505';
    notFound.style.fontSize = '0.85rem';
    notFound.style.fontStyle = 'italic';
    locationSensorInfo.appendChild(notFound);
  }

  locationSensorInformation.appendChild(locationSensorInfo);
  return locationSensorInformation;
};

/**
 * Builds wheel sensor info section
 * Uses API-enriched data with firmware version and neighbour report
 */
const buildWheelSensorInfo = (item) => {
  const wheelSensorInformation = document.createElement('div');
  wheelSensorInformation.className = 'form-group';

  const wheelSensorInfo = document.createElement('div');
  wheelSensorInfo.className = 'sensor-info';

  // Ensure Id is set
  if (!item.Id && item.sensorId) {
    item.Id = item.sensorId;
  }

  // Extract neighbour IDs from neighbourReport for labeling
  const nbIds = {
    nb1: item.neighbourReport?.nb1 || item.nb1 || null,
    nb2: item.neighbourReport?.nb2 || item.nb2 || null,
    nb3: item.neighbourReport?.nb3 || item.nb3 || null
  };

  // First pass: render standard wheel sensor fields (excluding neighbour report details)
  for (const key in item) {
    if (wheelSensorLabelMap.hasOwnProperty(key)) {
      let displayKey = wheelSensorLabelMap[key];

      // Special handling for unpaired sensors
      if (key === 'wheelId' && item[key] === '1180') {
        displayKey = 'Wheel Sensor Position (Unpaired)';
      }

      const field = buildField(displayKey, item[key]);
      wheelSensorInfo.appendChild(field);
    }
  }

  // Second pass: render neighbour reliability fields with IDs in labels
  const renderNeighbourReliability = (key, which) => {
    // Check both direct property and nested in neighbourReport
    const reliabilityValue = item[key] || item.neighbourReport?.[key];

    if (reliabilityValue != null && reliabilityValue !== '') {
      const labelBase = wheelSensorLabelMap[key] || which.toUpperCase();
      const id = nbIds[which];
      const displayKey = id ? `${labelBase} (${id})` : `${labelBase} (—)`;

      const field = buildField(displayKey, reliabilityValue);
      wheelSensorInfo.appendChild(field);
    }
  };

  // Render neighbour reliability fields in order
  renderNeighbourReliability('nb1reliability', 'nb1');
  renderNeighbourReliability('nb2reliability', 'nb2');
  renderNeighbourReliability('nb3reliability', 'nb3');

  wheelSensorInformation.appendChild(wheelSensorInfo);
  return wheelSensorInformation;
};


